import { useCanvasStore } from '../../store/canvas-store';
import { framesById } from '../../store/commands';
import { isPanelOwned } from '../../store/types';
import { resolveCfNode } from '../../store/slot-schema';
import type { RectLookup } from '../noteTarget';
import { commentTargetPoint } from '../../store/comments';

/**
 * Comment pins, rendered inside the world-transformed overlay (same container
 * as AnchorEdges) so x/y are canvas world coords. Click a pin to open its thread.
 */
export function CommentPins() {
  const frames = useCanvasStore((s) => s.doc.frames);
  const comments = useCanvasStore((s) => s.doc.comments) ?? [];
  const openId = useCanvasStore((s) => s.openCommentThreadId);
  const setOpen = useCanvasStore((s) => s.setOpenCommentThread);
  if (comments.length === 0) return null;

  const byId = framesById(frames.filter((f) => !isPanelOwned(f)));
  const rectLookup: RectLookup = (frameId, nodeId) => {
    if (typeof document === 'undefined') return null;
    const host = document.querySelector(`[data-frame-id="${CSS.escape(frameId)}"]`);
    const el = host ? resolveCfNode(host, nodeId) : null;
    const frame = byId.get(frameId);
    if (!el || !host || !frame) return null;
    const elRect = (el as HTMLElement).getBoundingClientRect();
    const hostRect = (host as HTMLElement).getBoundingClientRect();
    const zoom = useCanvasStore.getState().doc.viewport.zoom || 1;
    return {
      x: frame.x + (elRect.left - hostRect.left) / zoom,
      y: frame.y + (elRect.top - hostRect.top) / zoom,
      w: elRect.width / zoom,
      h: elRect.height / zoom,
    };
  };

  return (
    <>
      {comments.map((c) => {
        const pt = commentTargetPoint(c.target, byId, rectLookup);
        if (!pt) return null;
        const isOpen = c.id === openId;
        const count = c.messages.length;
        return (
          <button
            key={c.id}
            onClick={(e) => { e.stopPropagation(); setOpen(isOpen ? null : c.id); }}
            className={[
              'absolute -translate-x-1/2 -translate-y-full flex items-center justify-center',
              'min-w-[20px] h-[20px] px-1 rounded-full rounded-bl-none text-[11px] font-sans',
              'shadow-[var(--shadow-2)] cursor-pointer',
              c.resolved
                ? 'bg-[var(--color-surface)] text-[var(--color-text-muted)]'
                : 'bg-[var(--color-tool-note)] text-white',
              isOpen ? 'ring-2 ring-[var(--color-accent)]' : '',
            ].join(' ')}
            style={{ left: pt.x, top: pt.y }}
            title={c.resolved ? 'resolved' : `${count} message${count === 1 ? '' : 's'}`}
          >
            {count}
          </button>
        );
      })}
    </>
  );
}
